import React, { useCallback, useContext, useMemo, useState } from "react";
import { ActiveIssuesTable } from "./ActiveIssuesTable";
import { ActiveIssuesChart } from "./ActiveIssuesChart";
import { ActiveIssueFilters } from "components/ActiveIssues/ActiveIssuesFilters";
import {
  ActiveIssuesCaseData,
  LostEnergyResponseError
} from "./ActiveIssuesInterfaces";
import { Box, Divider, Typography } from "@mui/material";
import { AxiosContext } from "contexts/AxiosContext";
import { AxiosInstance } from "axios";
import { useQuery } from "@tanstack/react-query";

import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import useDumbPageTitle from "hooks/useDumbPageTitle";

const PAGE_TITLE = "Lost Energy Issue Tracker";

const fetchLostEnergy = async (
  axiosInstance: AxiosInstance
): Promise<ActiveIssuesCaseData[]> => {
  const res = await axiosInstance.get<ActiveIssuesCaseData[]>("lost_energy");
  return res.data ?? [];
};

interface LostEnergyFilters {
  siteIds: number[];
  statusTypeNames: string[];
}

const filterLostEnergyCases = (
  entry: ActiveIssuesCaseData,
  { siteIds, statusTypeNames }: LostEnergyFilters
): boolean => {
  const siteFilterIncludesEntry =
    siteIds.length === 0 || siteIds.includes(entry.site_id);
  const statusFilterIncludesEntry =
    statusTypeNames.length === 0 ||
    statusTypeNames.includes(entry.case_status_name);
  const lostEnergyIsPositiveInteger = entry.losses_to_date_mwh > 0;
  return (
    siteFilterIncludesEntry &&
    statusFilterIncludesEntry &&
    lostEnergyIsPositiveInteger
  );
};

export const ActiveIssues = (): JSX.Element => {
  useDumbPageTitle(PAGE_TITLE);

  const [selectedCaseId, setSelectedCaseId] = useState<number | undefined>(
    undefined
  );
  const { axios, loading: axiosLoading } = useContext(AxiosContext)!;
  const [filters, setFilters] = useState<{
    siteIds: number[];
    statusTypeNames: string[];
  }>({
    siteIds: [],
    statusTypeNames: []
  });

  const { isLoading, error, data: lostEnergyCases } = useQuery({
    queryKey: ["lostEnergy"],
    queryFn: () => fetchLostEnergy(axios),
    enabled: !axiosLoading,
    onError: (error: LostEnergyResponseError) => error,
    refetchOnMount: "always"
  });

  const filteredCases = useMemo(() => {
    return lostEnergyCases?.filter(lostEnergyCase =>
      filterLostEnergyCases(lostEnergyCase, filters)
    );
  }, [lostEnergyCases, filters]);

  // Updates the selected caseId from the chart or the table
  const handleSelectedCaseId = useCallback((caseId: number) => {
    setSelectedCaseId(prevSelecteCaseId => {
      return prevSelecteCaseId === caseId ? undefined : caseId;
    });
  }, []);

  return (
    <StyledPaper>
      <DescriptionSection>
        <Typography variant="body1">
          This page shows active cases that are likely causing energy loss
          across your fleet.
        </Typography>
        <Typography variant="body1">
          Lost energy is tracked on a per turbine basis, but is only assigned to
          a case if the machine status is set to offline or derated.
        </Typography>
      </DescriptionSection>
      <ActiveIssueFilters filters={filters} setFilters={setFilters} />
      <Divider style={{ margin: "20px" }} />
      {error ? (
        <ErrorWrapper>
          {error.response.data.detail ?? error.message}
        </ErrorWrapper>
      ) : (
        <FlexContainer>
          <>
            <ActiveIssuesTable
              loading={isLoading}
              lostEnergyCases={filteredCases ?? []}
              onChangeSelectedCaseId={handleSelectedCaseId}
              selectedCaseId={selectedCaseId}
            />
            <ActiveIssuesChart
              lostEnergyCases={filteredCases}
              onChangeSelectedCaseId={handleSelectedCaseId}
              selectedCaseId={selectedCaseId}
            />
          </>
        </FlexContainer>
      )}
    </StyledPaper>
  );
};

const DescriptionSection = styled(Box)`
  margin-top: 10px;
`;

const StyledPaper = styled(Paper)`
  padding: 16px;
  height: 100%;
`;

const FlexContainer = styled("div")`
  display: flex;
  flex-wrap: wrap-reverse;
  overflow: hidden;
`;

const ErrorWrapper = styled("div")`
  display: flex;
  justify-content: space-around;
`;
